import Vue from 'vue';
import VueRouter from 'vue-router';
import store from '../store/store';
import routes from './routes';
import routeConstants from './routes.constants';
import { router } from '@/main';
import { saveScrollPosition } from '@/utils/scrollPosition';
import { consumeForcedReloadPath } from '@/utils/versionWatch';
import {
  clearPendingScrollIntent,
  getPendingScrollIntent,
  setPendingScrollIntent
} from './scrollIntent';
// import { isCountry } from '../plugins/country/module';

// Swallow "Redirected" / "Cancelled" / "Duplicated" navigation failures from
// $router.push|replace. These are expected when a beforeEach guard redirects
// (e.g. the auth gate replacing the route with the login page) -- the
// navigation succeeds, the original push's promise just rejects with a
// NavigationFailure that has nowhere to be caught at the call site.
['push', 'replace'].forEach(method => {
  const original = VueRouter.prototype[method];
  VueRouter.prototype[method] = function patchedPush(
    location,
    onResolve,
    onReject
  ) {
    if (onResolve || onReject) {
      return original.call(this, location, onResolve, onReject);
    }
    return original.call(this, location).catch(err => {
      if (VueRouter.isNavigationFailure && VueRouter.isNavigationFailure(err)) {
        return err;
      }
      return Promise.reject(err);
    });
  };
});

// Pending scroll intent for App.vue to handle after transition (see
// ./scrollIntent.js).
export { getPendingScrollIntent, clearPendingScrollIntent };

export default function initRouter() {
  Vue.use(VueRouter);

  const router = new VueRouter({
    mode: 'history',
    history: true,
    routes: getEnableRoutesOnly(routes),
    linkActiveClass: 'active',
    linkExactActiveClass: 'exact-active',
    // Return false to disable Vue Router's scroll handling.
    // We handle scrolling manually in App.vue after the transition completes,
    // which is required for iOS Safari compatibility.
    scrollBehavior(to, from, savedPosition) {
      // Exclude Matches pages - they handle their own scroll management
      const isMatchesRoute =
        to.path.includes(routeConstants.MATCHES) ||
        from.path.includes(routeConstants.MATCHES);

      if (isMatchesRoute) {
        setPendingScrollIntent({ type: 'none' });
        return false;
      }

      // Don't scroll when only opening/closing modals (query param changes on same path)
      const isModalChange =
        to.path === from.path &&
        to.query.propositionPageId !== from.query.propositionPageId;

      if (isModalChange) {
        setPendingScrollIntent({ type: 'none' });
        return false;
      }

      // Don't scroll-to-top when the destination owns its own scroll
      // target (e.g. EditProposition2 scrolling to ?section=..., or the
      // legacy photos URL which scrolls to its photos section).
      if (
        to.query.section ||
        to.path.startsWith(routeConstants.EDIT_PROPOSITION_PHOTOS + '/')
      ) {
        setPendingScrollIntent({ type: 'none' });
        return false;
      }

      // Determine scroll intent for App.vue to handle after transition
      if (savedPosition) {
        // Back/forward navigation - restore saved position
        setPendingScrollIntent({ type: 'restore', route: to });
      } else if (to.hash) {
        // Hash navigation - scroll to element
        setPendingScrollIntent({ type: 'hash', hash: to.hash });
      } else {
        // New navigation - scroll to top
        setPendingScrollIntent({ type: 'top' });
      }

      // Return false - App.vue handles actual scrolling after transition
      return false;
    }
  });

  router.beforeEach(async (to, from, next) => {
    // A deploy has happened since this tab loaded (see versionWatch) — the
    // old bundle's lazy chunks are gone, so turn this navigation into a
    // full page load onto the fresh bundle instead of letting a stale
    // chunk request break it (Sentry KL-7 cluster).
    const forcedReloadPath = consumeForcedReloadPath(to, from);
    if (forcedReloadPath) {
      const browserPath =
        window.location.pathname +
        window.location.search +
        window.location.hash;
      if (browserPath === forcedReloadPath) {
        // Back/forward: the browser URL has already moved to the target, so
        // assign() would be a same-document no-op for hash targets and
        // next(false) would corrupt history (vue-router's ensureURL pushes
        // the from-URL back). A plain reload is always a real document load
        // and adds no history entries. The page is being torn down, so the
        // pending navigation is intentionally left unresolved.
        window.location.reload();
        return;
      }
      window.location.assign(forcedReloadPath);
      next(false);
      return;
    }

    // Save scroll position before navigating away (for back/forward restoration)
    if (from.path !== to.path) {
      saveScrollPosition(from);
    }

    store.dispatch('menu/closeMenu');

    if (to.meta?.group !== 'signup') {
      store.dispatch('signup/setInSignup', { inSignup: false });
    } else {
      store.dispatch('signup/setInSignup', { inSignup: true });
    }

    if (to.path !== from.path) {
      store.commit('ui/removeAllDisableScroll');
    }

    await checkAuthentication(to, next);
  });

  // The proposition modal follows the confirmed route, not one that is
  // still being checked: a back before the ad's URL is shown cancels that
  // navigation, and a modal opened for it would have no URL to close.
  router.afterEach((to, from) => {
    if (to.query.propositionPageId) {
      store.dispatch('ui/openPropositionModal', {
        propositionId: Number(to.query.propositionPageId),
        propositionId2: to.query.propositionPageId2
          ? Number(to.query.propositionPageId2)
          : null,
        type: to.query.type
      });
    } else if (from.query.propositionPageId) {
      store.dispatch('ui/closePropositionModal', false);
    }
  });

  return router;
}

let readyCheckIval;
async function checkAuthentication(to, next) {
  const requireAuthentication = !to.meta || !to.meta.public;

  if (!requireAuthentication) {
    next();
    return;
  }

  const proceedWhenReady = () => {
    if (!store.getters['app/ready']) return false;
    clearInterval(readyCheckIval);

    const isAuthenticated = store.getters['app/isAuthenticated'];
    if (!isAuthenticated && to.path !== routeConstants.LOGIN) {
      router.replace(`${routeConstants.LOGIN}?returnUrl=${to.path}`);
    }
    next();
    return true;
  };

  clearInterval(readyCheckIval);
  // Once the app is ready, don't wait for a poll: until a navigation
  // completes, a back or another tap can cancel it.
  if (proceedWhenReady()) return;
  readyCheckIval = setInterval(proceedWhenReady, 10);
}

function getEnableRoutesOnly(routesArray) {
  return routesArray.filter(route => !route.meta || !route.meta.disabled);
}
